/** @file defaultStyleDefs
 * From https://github.com/tldraw/tldraw/blob/8bbfe91e8805198827a48c5410d7e741f416ac1d/packages/tldraw/src/lib/shapes/shared/defaultStyleDefs.tsx
 */
import type { SvgExportDef, TLDefaultColorTheme, TLDefaultFillStyle, TLDefaultFontStyle } from '@tldraw/editor'
import { DefaultFontFamilies, DefaultFontStyle, FileHelpers } from '@tldraw/editor'
import { useDefaultColorTheme } from 'tldraw'

function findFont(name: TLDefaultFontStyle): FontFace | null {
  const fontFamily = DefaultFontFamilies[name]
  for (const font of document.fonts) {
    if (fontFamily.includes(font.family)) {
      return font
    }
  }
  return null
}

export function getFontDefForExport(fontStyle: TLDefaultFontStyle): SvgExportDef {
  return {
    key: `${DefaultFontStyle.id}:${fontStyle}`,
    async getElement() {
      const font = findFont(fontStyle)
      if (font == null) return null

      const url: string = (font as any).$$_url
      const fontFaceRule: string = (font as any).$$_fontface
      if (!url || !fontFaceRule) return null

      const fontFile = await (await fetch(url)).blob()
      const base64FontFile = await FileHelpers.blobToDataUrl(fontFile)

      const newFontFaceRule = fontFaceRule.replace(url, base64FontFile)
      return <style>{newFontFaceRule}</style>
    },
  }
}

function getPatternLodForZoomLevel(zoom: number): number {
  return Math.ceil(Math.log2(Math.max(1, zoom)))
}

export function getHashPatternZoomName(zoom: number, theme: TLDefaultColorTheme['id']): string {
  const lod = getPatternLodForZoomLevel(zoom)
  return `tldraw_hash_pattern_${theme}_${lod}`
}

function HashPatternForExport(): JSX.Element {
  const theme = useDefaultColorTheme()
  const t = 8 / 12
  return (
    <>
      <mask id="hash_pattern_mask">
        <rect x="0" y="0" width="8" height="8" fill="white" />
        <g strokeLinecap="round" stroke="black">
          <line x1={t * 1} y1={t * 3} x2={t * 3} y2={t * 1} />
          <line x1={t * 5} y1={t * 7} x2={t * 7} y2={t * 5} />
          <line x1={t * 9} y1={t * 11} x2={t * 11} y2={t * 9} />
        </g>
      </mask>
      <pattern id={getHashPatternZoomName(1, theme.id)} width="8" height="8" patternUnits="userSpaceOnUse">
        <rect x="0" y="0" width="8" height="8" fill={theme.solid} mask="url(#hash_pattern_mask)" />
      </pattern>
    </>
  )
}

export function getFillDefForExport(fill: TLDefaultFillStyle): SvgExportDef {
  return {
    key: `${DefaultFontStyle.id}:${fill}`,
    async getElement() {
      if (fill !== 'pattern') return null

      return <HashPatternForExport />
    },
  }
}
